import { useState } from "react"
import {
	Paper,
	Grid,
	Container,
	Box,
	Divider,
	Hidden,
	Dialog,
	DialogActions,
	DialogContent,
	useMediaQuery,
} from "@mui/material"
import { makeStyles } from "makeStyles"
import { InstantSearch, Configure } from "react-instantsearch-dom"
import Icon from "@mdi/react"
import { mdiFilter } from "@mdi/js"
import { ParsedQs } from "qs"
import dynamic from "next/dynamic"

import Button from "components/Buttons/Button"
import CategoryInformation from "./CategoryInformation"
import FiltersSideBar from "components/Search/Filters/FiltersSidebar"
import ListView from "components/Search/Results/ListView"
import GridView from "components/Search/Results/GridView"
import NumberOfResults from "components/Search/Results/NumberOfResults"
import ToggleResultsView from "components/Search/Results/ToggleResultsView"
import SortResults from "components/Search/Results/SortResults"
import SeoContent from "components/SeoContent"
const Pagination = dynamic(() => import("./Pagination"), {
	ssr: false,
})
import Help from "./Help"
import Information from "../IntermediateCategoryPage/Information"
import SubCategoryList from "./SubCategoryList"
import BreadCrumbs, { BreadCrumbAncestor } from "components/BreadCrumbs"
import VirtualRefinements from "./VirtualRefinements"

import useToggle from "@secureo/common/hooks/useToggle"
import useInputValue from "hooks/useInputValue"
import useAlgoliaURLSync from "@secureo/common/hooks/useAlgoliaURLSync"
import useTranslation from "hooks/useTranslation"

import getCategoryBreadCrumbs from "utils/formatters/getCategoryBreadCrumbs"
import getValueFromLocalizedStringWithFallback from "utils/i18n/getValueFromLocalizedStringWithFallback"

import searchClient from "utils/algolia/searchClient"
import { categoryPageFilters } from "config/filterConfig"

import { SearchState } from "react-instantsearch-core"
import { Category } from "typings/Category"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()((theme) => ({
	paper: {
		padding: theme.spacing(0),
	},
	sectionMobile: {
		[theme.breakpoints.down("md")]: {
			display: "none",
		},
	},
	configRoot: {
		flexGrow: 1,
	},
	divider: {
		marginTop: "20px",
	},
	mobileSticky: {
		position: "sticky",
		top: "0px",
		background: "#F7FAFA",
		width: "100%",
		zIndex: 2,
	},
}))

const hitsPerPageConfig = {
	listView: {
		defaultRefinement: 16,
		items: [
			{ value: 16, label: "16" },
			{ value: 18, label: "18" },
			{ value: 27, label: "29" },
		],
	},
	gridView: {
		defaultRefinement: 16,
		items: [
			{ value: 16, label: "16" },
			{ value: 16, label: "16" },
			{ value: 24, label: "24" },
		],
	},
}

const CategoryPageContainer = ({
	category,
	referencedCategoryId,
	referencedCategory,
	parsedInitialFilterValues,
	resultsState,
	initialSearchState = {},
	// eslint-disable-next-line
	onSearchParameters = () => {},
	indexName,
	unsortedIndexName,
	locale,
	language,
	isBrandCategory = false,
	widgetsCollector,
	countryCode,
}: Props) => {
	const [isListView, toggleIsListView] = useToggle(true)
	const hitsPerPage = useInputValue("16")
	const { classes } = useStyles()
	const [open, setOpen] = useState(false)
	const isSmallerThan768pxs = useMediaQuery("(min-width: 768.98px)")
	const { t } = useTranslation("category")

	const { slug } = category
	const name = getValueFromLocalizedStringWithFallback(category.nameAllLocales, locale)

	const breadCrumb: BreadCrumbAncestor = {
		label: name,
		href: `/c/${slug}`,
	}
	const breadCrumbsPaths = isBrandCategory
		? [
				{
					label: t("brands"),
					href: "/brands",
				},
			]
		: getCategoryBreadCrumbs(
				referencedCategory
					? {
							...category,
							parent: referencedCategory,
						}
					: category,
				locale,
			)

	const handleClickOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
	}

	const [searchState, onSearchStateChange] = useAlgoliaURLSync(
		initialSearchState,
		isBrandCategory ? "/brands/[slug]" : "/c/[slug]",
		isBrandCategory ? `/brands/${slug}` : `/c/${slug}`,
	)

	const selectedHistPerPageConfig = isListView
		? hitsPerPageConfig.listView
		: hitsPerPageConfig.gridView

	const sortingConfig = {
		defaultRefinement: indexName,
		options: [
			{
				value: indexName,
				label: t("mostSold"),
			},
			{
				value: `${unsortedIndexName}_price_asc`,
				label: t("priceAscending"),
			},
			{
				value: `${unsortedIndexName}_price_desc`,
				label: t("priceDescending"),
			},
		],
	}

	return (
		<>
			<Box style={{ marginTop: "0px", paddingBottom: "10px" }}>
				<BreadCrumbs breadCrumbsPaths={breadCrumbsPaths} currentPath={breadCrumb} />
			</Box>
			<br />
			<Box mb={5} style={{ marginTop: "1vw" }}>
				<InstantSearch
					indexName={indexName}
					searchClient={searchClient}
					resultsState={resultsState}
					searchState={searchState}
					onSearchParameters={onSearchParameters}
					onSearchStateChange={onSearchStateChange}
					widgetsCollector={widgetsCollector}
				>
					<Configure hitsPerPage={hitsPerPage.value} />
					<VirtualRefinements
						categoryId={referencedCategoryId}
						isBrandCategory={isBrandCategory}
						brandName={name}
						parsedInitialFilterValues={parsedInitialFilterValues}
					/>

					<Container>
						<Grid container spacing={3}>
							<Grid item xs={12} md={3}>
								<div className={classes.sectionMobile}>
									<Paper className={classes.paper}>
										<FiltersSideBar
											filters={categoryPageFilters}
											brandName={name}
											isBrandCategory={isBrandCategory}
											parsedInitialFilterValues={parsedInitialFilterValues}
										/>
									</Paper>
									<Information />
								</div>
							</Grid>
							<Grid item xs={12} md={9}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<CategoryInformation
											category={category}
											locale={locale}
											isBrandCategory={isBrandCategory}
										/>
										<SubCategoryList
											category={category}
											countryCode={countryCode}
											locale={locale}
										/>

										<div className={classes.configRoot}>
											<Divider className={classes.divider} />
											<Box pt={3}>
												<div
													className={classes.mobileSticky}
													style={{
														position: isSmallerThan768pxs
															? "relative"
															: "sticky",
														zIndex: isSmallerThan768pxs ? 0 : 2,
													}}
												>
													<Grid container spacing={3}>
														<Hidden mdUp>
															<Grid item xs={6}>
																<Box mt={1}>
																	<Button
																		color="cart"
																		style={{ height: "40px" }}
																		onClick={handleClickOpen}
																	>
																		<Icon
																			path={mdiFilter}
																			size={0.8}
																		/>{" "}
																		{t("toFilter")}
																	</Button>
																	<Dialog
																		open={open}
																		keepMounted
																		onClose={handleClose}
																		aria-labelledby="alert-dialog-slide-title"
																		aria-describedby="alert-dialog-slide-description"
																	>
																		<DialogContent
																			style={{
																				padding: "0",
																				margin: "0",
																			}}
																		>
																			<FiltersSideBar
																				filters={
																					categoryPageFilters
																				}
																				brandName={name}
																				isBrandCategory={
																					isBrandCategory
																				}
																				parsedInitialFilterValues={
																					parsedInitialFilterValues
																				}
																			/>
																		</DialogContent>
																		<DialogActions>
																			<Button
																				onClick={
																					handleClose
																				}
																				color="primary"
																			>
																				{t("cancel")}
																			</Button>
																			<Button
																				onClick={
																					handleClose
																				}
																				color="primary"
																			>
																				{t("toFilter")}
																			</Button>
																		</DialogActions>
																	</Dialog>
																</Box>
															</Grid>
														</Hidden>
														<SortResults
															defaultRefinement={
																sortingConfig.defaultRefinement
															}
															items={sortingConfig.options}
														/>
														<ToggleResultsView
															toggleIsListView={toggleIsListView}
														/>
														<Hidden mdDown>
															<NumberOfResults
																defaultRefinement={
																	selectedHistPerPageConfig.defaultRefinement
																}
																items={
																	selectedHistPerPageConfig.items
																}
															/>
														</Hidden>
													</Grid>
												</div>
												<Hidden mdUp>
													<Box my={2}>
														<NumberOfResults
															defaultRefinement={
																selectedHistPerPageConfig.defaultRefinement
															}
															items={selectedHistPerPageConfig.items}
														/>
													</Box>
												</Hidden>
												<Grid item xs={12}>
													<Box mt={2}>
														{isListView ? (
															<ListView categoryName={name} />
														) : (
															<GridView categoryName={name} />
														)}
													</Box>
												</Grid>
												<Grid item xs={12}>
													<Pagination />
												</Grid>
											</Box>
										</div>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Help />
						<SeoContent category={category} language={language} />
					</Container>
				</InstantSearch>
			</Box>
		</>
	)
}

export interface Props {
	category: Category
	referencedCategory?: Category
	referencedCategoryId: string
	parsedInitialFilterValues: ParsedQs
	indexName: string
	unsortedIndexName: string
	resultsState?: any
	initialSearchState?: SearchState
	onSearchParameters?: () => void
	isBrandCategory?: boolean
	widgetsCollector?: any
	locale: string
	language: string
	countryCode: CountryCode
}

export default CategoryPageContainer
